import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  X, ChevronDown, ChevronUp, Lock, Check, Filter,
  Utensils, Coffee, IceCreamCone, Cake, Scissors, ShoppingBag, Gem,
  Store, ShoppingCart, Tv, Dog, Dumbbell, Car, Home, Baby,
  Cannabis, Target, Users, Plane, Sparkles, Moon, TreePine, HelpCircle,
} from "lucide-react";

const ALL_CATEGORIES = [
  "Restaurant", "Fast Food", "Cafes & Drinks", "Bakery", "Dessert & Ice Cream",
  "Beauty & Skincare", "Clothing & Apparel", "Jewelry & Accessories", "Retail",
  "Grocery", "Entertainment", "Pet", "Health & Fitness", "Auto", "Home & Garden",
  "Family", "Cannabis and Dispensaries", "Firearms and Ranges", "Adult Entertainment",
  "Experiences and Travel", "Wellness and Spa", "Nightlife", "Outdoor and Recreation", "Other",
];

const AGE_RESTRICTED_CATEGORIES = {
  "Cannabis and Dispensaries": 21, "Adult Entertainment": 18, "Firearms and Ranges": 18, "Nightlife": 21,
};

const CAT_ICONS = {
  "Restaurant": Utensils, "Fast Food": Utensils, "Cafes & Drinks": Coffee,
  "Bakery": Cake, "Dessert & Ice Cream": IceCreamCone, "Beauty & Skincare": Scissors,
  "Clothing & Apparel": ShoppingBag, "Jewelry & Accessories": Gem, "Retail": Store,
  "Grocery": ShoppingCart, "Entertainment": Tv, "Pet": Dog,
  "Health & Fitness": Dumbbell, "Auto": Car, "Home & Garden": Home,
  "Family": Baby, "Cannabis and Dispensaries": Cannabis, "Firearms and Ranges": Target,
  "Adult Entertainment": Lock, "Experiences and Travel": Plane,
  "Wellness and Spa": Sparkles, "Nightlife": Moon, "Outdoor and Recreation": TreePine,
  "Other": HelpCircle,
};

const CAT_COLORS = {
  "Restaurant": { bg: "bg-orange-50", sel: "bg-orange-500", text: "text-orange-600" },
  "Fast Food": { bg: "bg-red-50", sel: "bg-red-500", text: "text-red-600" },
  "Cafes & Drinks": { bg: "bg-amber-50", sel: "bg-amber-600", text: "text-amber-700" },
  "Bakery": { bg: "bg-yellow-50", sel: "bg-yellow-600", text: "text-yellow-700" },
  "Dessert & Ice Cream": { bg: "bg-pink-50", sel: "bg-pink-500", text: "text-pink-600" },
  "Beauty & Skincare": { bg: "bg-rose-50", sel: "bg-rose-500", text: "text-rose-600" },
  "Clothing & Apparel": { bg: "bg-violet-50", sel: "bg-violet-500", text: "text-violet-600" },
  "Jewelry & Accessories": { bg: "bg-purple-50", sel: "bg-purple-500", text: "text-purple-600" },
  "Retail": { bg: "bg-blue-50", sel: "bg-blue-500", text: "text-blue-600" },
  "Grocery": { bg: "bg-green-50", sel: "bg-green-500", text: "text-green-600" },
  "Entertainment": { bg: "bg-indigo-50", sel: "bg-indigo-500", text: "text-indigo-600" },
  "Pet": { bg: "bg-teal-50", sel: "bg-teal-500", text: "text-teal-600" },
  "Health & Fitness": { bg: "bg-lime-50", sel: "bg-lime-600", text: "text-lime-700" },
  "Auto": { bg: "bg-slate-100", sel: "bg-slate-600", text: "text-slate-600" },
  "Home & Garden": { bg: "bg-emerald-50", sel: "bg-emerald-500", text: "text-emerald-600" },
  "Family": { bg: "bg-sky-50", sel: "bg-sky-500", text: "text-sky-600" },
  "Cannabis and Dispensaries": { bg: "bg-green-50", sel: "bg-green-700", text: "text-green-700" },
  "Firearms and Ranges": { bg: "bg-stone-100", sel: "bg-stone-600", text: "text-stone-600" },
  "Adult Entertainment": { bg: "bg-red-50", sel: "bg-red-700", text: "text-red-700" },
  "Experiences and Travel": { bg: "bg-cyan-50", sel: "bg-cyan-500", text: "text-cyan-600" },
  "Wellness and Spa": { bg: "bg-fuchsia-50", sel: "bg-fuchsia-500", text: "text-fuchsia-600" },
  "Nightlife": { bg: "bg-violet-50", sel: "bg-violet-600", text: "text-violet-600" },
  "Outdoor and Recreation": { bg: "bg-emerald-50", sel: "bg-emerald-600", text: "text-emerald-700" },
  "Other": { bg: "bg-slate-50", sel: "bg-slate-500", text: "text-slate-500" },
};

const GROUP_OPTIONS = [
  { value: "solo", label: "Solo" }, { value: "couple", label: "Couple" },
  { value: "small_group", label: "Small Group" }, { value: "large_group", label: "Large Group" },
  { value: "family_with_kids", label: "Family with Kids" },
  { value: "adults_only", label: "Adults Only" }, { value: "seniors", label: "Seniors" },
];

const SEASON_OPTIONS = [
  { value: "year_round", label: "Year Round" }, { value: "spring", label: "Spring" },
  { value: "summer", label: "Summer" }, { value: "fall", label: "Fall" },
  { value: "winter", label: "Winter" }, { value: "holiday_season", label: "Holiday Season" },
  { value: "valentines_week", label: "Valentine's Week" },
];
const TIME_OPTIONS = [
  { value: "weekend_only", label: "Weekend Only" }, { value: "weekday_only", label: "Weekday Only" },
  { value: "lunch_hours", label: "Lunch Hours" }, { value: "dinner_hours", label: "Dinner Hours" },
  { value: "late_night", label: "Late Night" },
];

const VIBE_OPTIONS = [
  { value: "relaxed_quiet", label: "Relaxed & Quiet", desc: "Perfect for a chill birthday", emoji: "\u{1F9D8}" },
  { value: "casual_fun", label: "Casual & Fun", desc: "Laid back good times", emoji: "\u{1F604}" },
  { value: "lively_social", label: "Lively & Social", desc: "Energy and people", emoji: "\u{1F389}" },
  { value: "high_energy", label: "High Energy", desc: "Get moving", emoji: "\u{26A1}" },
  { value: "upscale_elegant", label: "Upscale & Elegant", desc: "Treat yourself", emoji: "\u{1F942}" },
  { value: "unique_unusual", label: "Unique & Unusual", desc: "Something different", emoji: "\u{1F3AD}" },
];

const PRICE_OPTIONS = [
  { value: "free_offer", label: "Free Offer", icon: "\u{1F381}" },
  { value: "budget", label: "Budget", icon: "\u{1F4B0}" },
  { value: "moderate", label: "Moderate", icon: "\u{1F4B0}\u{1F4B0}" },
  { value: "upscale", label: "Upscale", icon: "\u{1F4B0}\u{1F4B0}\u{1F4B0}" },
  { value: "luxury", label: "Luxury", icon: "\u{1F4B0}\u{1F4B0}\u{1F4B0}\u{1F4B0}" },
];

const LOGISTICS_OPTIONS = [
  { value: "reservation_required", label: "Reservation Required" },
  { value: "walk_in_friendly", label: "Walk-In Friendly" },
  { value: "outdoor_seating", label: "Outdoor Seating" },
  { value: "indoor_only", label: "Indoor Only" },
  { value: "wheelchair_accessible", label: "Wheelchair Accessible" },
  { value: "parking_available", label: "Parking Available" },
  { value: "transit_nearby", label: "Transit Nearby" },
  { value: "pet_friendly", label: "Pet Friendly" },
];

const SESSION_KEY = "bdl_filters_v531";

function Section({ title, count, defaultOpen, children, testId }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="border-b border-slate-100 pb-3">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-2" data-testid={testId}>
        <span className="text-sm font-semibold text-slate-700">{title}
          {count > 0 && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{count}</span>}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      <AnimatePresence>{open && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>{children}</motion.div>}</AnimatePresence>
    </div>
  );
}

export default function MultiDimensionalFilterSheet({
  isOpen, onClose, onApply, initialFilters = {},
  // Existing dietary/offer props passed through
  existingFilterProps = {},
}) {
  const { user } = useAuth();
  const { theme } = useTheme();

  // Calculate user age for restricted categories
  const userAge = (() => {
    const bday = user?.birthday;
    if (!bday) return null;
    const d = new Date(bday);
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
    return age;
  })();

  // Local state for all filters (held until Apply)
  const [local, setLocal] = useState({
    categories: [], groupProfile: [], seasonalTags: [], vibe: "",
    priceTier: "", logisticsFlags: [], offerQuality: "",
    verifiedOnly: false, newOnly: false, openNow: false,
    // Existing filters
    halalFilter: false, halalCertifiedFilter: false, veganFilter: false,
    vegetarianFilter: false, glutenFreeFilter: false,
    ...initialFilters,
  });

  useEffect(() => {
    setLocal(prev => ({ ...prev, ...initialFilters }));
  }, [isOpen]);

  const toggleArray = (key, value) => {
    setLocal(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const countFor = (key) => {
    const v = local[key];
    if (Array.isArray(v)) return v.length;
    if (typeof v === "boolean") return v ? 1 : 0;
    return v ? 1 : 0;
  };

  const totalActive = local.categories.length + local.groupProfile.length + local.seasonalTags.length
    + (local.vibe ? 1 : 0) + (local.priceTier ? 1 : 0) + local.logisticsFlags.length
    + (local.verifiedOnly ? 1 : 0) + (local.newOnly ? 1 : 0) + (local.openNow ? 1 : 0);

  const handleApply = () => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(local));
    onApply(local);
    onClose();
  };

  const handleReset = () => {
    setLocal({
      categories: [], groupProfile: [], seasonalTags: [], vibe: "",
      priceTier: "", logisticsFlags: [], offerQuality: "",
      verifiedOnly: false, newOnly: false, openNow: false,
      halalFilter: false, halalCertifiedFilter: false, veganFilter: false,
      vegetarianFilter: false, glutenFreeFilter: false,
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-[200]" onClick={onClose} />
      <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="fixed bottom-0 left-0 right-0 z-[210] bg-white rounded-t-3xl max-h-[92vh] flex flex-col" data-testid="multi-filter-sheet">
        <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-slate-300" /></div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-800">
            <Filter className="w-4 h-4 inline mr-1.5 text-amber-500" />Filters
            {totalActive > 0 && <span className="ml-2 text-xs font-medium text-amber-600">({totalActive} active)</span>}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="filter-close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
          {/* ── CATEGORY ── */}
          <Section title="Category" count={local.categories.length} defaultOpen={true} testId="section-category">
            <div className="grid grid-cols-2 gap-1.5 pb-2">
              {ALL_CATEGORIES.map(cat => {
                const restricted = AGE_RESTRICTED_CATEGORIES[cat];
                const hidden = restricted && userAge !== null && userAge < restricted;
                if (hidden) return null;
                const selected = local.categories.includes(cat);
                const Icon = CAT_ICONS[cat] || HelpCircle;
                const colors = CAT_COLORS[cat] || { bg: "bg-slate-50", sel: "bg-amber-500", text: "text-slate-600" };
                return (
                  <button key={cat} onClick={() => toggleArray("categories", cat)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${selected ? `${colors.sel} text-white` : `${colors.bg} ${colors.text} hover:opacity-80`}`}
                    data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{cat}</span>
                    {restricted && !hidden && <Lock className="w-3 h-3 ml-auto flex-shrink-0 opacity-50" />}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ── WHO / GROUP PROFILE ── */}
          <Section title="Who" count={local.groupProfile.length} testId="section-who">
            <div className="flex flex-wrap gap-1.5 pb-2">
              {GROUP_OPTIONS.map(opt => {
                const sel = local.groupProfile.includes(opt.value);
                return <button key={opt.value} onClick={() => toggleArray("groupProfile", opt.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sel ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-600"}`} data-testid={`group-${opt.value}`}>{opt.label}</button>;
              })}
            </div>
          </Section>

          {/* ── WHEN / SEASONAL ── */}
          <Section title="When" count={local.seasonalTags.length} testId="section-when">
            <div className="space-y-2 pb-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase">Season</p>
              <div className="flex flex-wrap gap-1.5">
                {SEASON_OPTIONS.map(opt => {
                  const sel = local.seasonalTags.includes(opt.value);
                  return <button key={opt.value} onClick={() => toggleArray("seasonalTags", opt.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sel ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-600"}`} data-testid={`season-${opt.value}`}>{opt.label}</button>;
                })}
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">Time</p>
              <div className="flex flex-wrap gap-1.5">
                {TIME_OPTIONS.map(opt => {
                  const sel = local.seasonalTags.includes(opt.value);
                  return <button key={opt.value} onClick={() => toggleArray("seasonalTags", opt.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sel ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-600"}`} data-testid={`time-${opt.value}`}>{opt.label}</button>;
                })}
              </div>
            </div>
          </Section>

          {/* ── VIBE ── */}
          <Section title="Vibe" count={local.vibe ? 1 : 0} testId="section-vibe">
            <div className="grid grid-cols-2 gap-2 pb-2">
              {VIBE_OPTIONS.map(v => {
                const sel = local.vibe === v.value;
                return (
                  <button key={v.value} onClick={() => setLocal(prev => ({ ...prev, vibe: sel ? "" : v.value }))}
                    className={`p-3 rounded-xl text-left transition-all border-2 ${sel ? "border-amber-500 bg-amber-50" : "border-slate-100 bg-white hover:border-slate-200"}`}
                    data-testid={`vibe-${v.value}`}>
                    <div className="text-lg mb-0.5">{v.emoji}</div>
                    <div className="text-xs font-semibold text-slate-700">{v.label}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{v.desc}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ── PRICE TIER ── */}
          <Section title="Price Tier" count={local.priceTier ? 1 : 0} testId="section-price">
            <div className="flex flex-wrap gap-1.5 pb-2">
              {PRICE_OPTIONS.map(p => {
                const sel = local.priceTier === p.value;
                return <button key={p.value} onClick={() => setLocal(prev => ({ ...prev, priceTier: sel ? "" : p.value }))} className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${sel ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-600"}`} data-testid={`price-${p.value}`}>{p.icon} {p.label}</button>;
              })}
            </div>
          </Section>

          {/* ── DIETARY ── */}
          <Section title="Dietary" count={[local.halalFilter, local.halalCertifiedFilter, local.veganFilter, local.vegetarianFilter, local.glutenFreeFilter].filter(Boolean).length} testId="section-dietary">
            <div className="flex flex-wrap gap-2 pb-2">
              {[
                { key: "halalCertifiedFilter", label: "Halal Certified", bg: "bg-green-500", off: "bg-green-50 text-green-700 border-green-200" },
                { key: "halalFilter", label: "Halal Friendly", bg: "bg-green-400", off: "bg-green-50/70 text-green-600 border-green-200/70" },
                { key: "veganFilter", label: "Vegan", bg: "bg-lime-500", off: "bg-lime-50 text-lime-700 border-lime-200" },
                { key: "vegetarianFilter", label: "Vegetarian", bg: "bg-emerald-500", off: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                { key: "glutenFreeFilter", label: "Gluten Free", bg: "bg-amber-500", off: "bg-amber-50 text-amber-700 border-amber-200" },
              ].map(f => {
                const active = local[f.key];
                return <button key={f.key} onClick={() => setLocal(prev => ({ ...prev, [f.key]: !prev[f.key] }))} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${active ? `${f.bg} text-white` : `${f.off} border`}`} data-testid={`dietary-${f.key}`}>{f.label}</button>;
              })}
            </div>
          </Section>

          {/* ── LOGISTICS ── */}
          <Section title="Logistics" count={local.logisticsFlags.length} testId="section-logistics">
            <div className="grid grid-cols-2 gap-1.5 pb-2">
              {LOGISTICS_OPTIONS.map(opt => {
                const sel = local.logisticsFlags.includes(opt.value);
                return (
                  <button key={opt.value} onClick={() => toggleArray("logisticsFlags", opt.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${sel ? "bg-amber-500 text-white" : "bg-slate-50 text-slate-600"}`}
                    data-testid={`logistics-${opt.value}`}>
                    {sel && <Check className="w-3 h-3 flex-shrink-0" />}
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ── QUICK TOGGLES ── */}
          <Section title="Quick Toggles" count={(local.verifiedOnly ? 1 : 0) + (local.newOnly ? 1 : 0) + (local.openNow ? 1 : 0)} testId="section-quick-toggles">
            <div className="space-y-2 pb-2">
              {[
                { key: "verifiedOnly", label: "Verified Offers Only", desc: "Only show offers confirmed by real users" },
                { key: "newOnly", label: "New to BirthdayDeaLight", desc: "Businesses added in the last 30 days" },
                { key: "openNow", label: "Open Right Now", desc: "Only show businesses currently open" },
              ].map(t => (
                <button key={t.key} onClick={() => setLocal(prev => ({ ...prev, [t.key]: !prev[t.key] }))}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all ${local[t.key] ? "bg-amber-50 border border-amber-300" : "bg-slate-50 border border-transparent"}`}
                  data-testid={`toggle-${t.key}`}>
                  <div>
                    <div className={`text-xs font-semibold ${local[t.key] ? "text-amber-700" : "text-slate-700"}`}>{t.label}</div>
                    <div className="text-[10px] text-slate-400">{t.desc}</div>
                  </div>
                  <div className={`w-10 h-5 rounded-full transition-all ${local[t.key] ? "bg-amber-500" : "bg-slate-200"} relative`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${local[t.key] ? "translate-x-5" : "translate-x-0.5"}`} />
                  </div>
                </button>
              ))}
            </div>
          </Section>
        </div>

        {/* ── BOTTOM ACTIONS ── */}
        <div className="border-t border-slate-100 px-5 py-4 bg-white flex gap-3">
          <button onClick={handleReset} className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 transition" data-testid="filter-reset-btn">Reset All</button>
          <button onClick={handleApply} className="flex-1 py-3 rounded-2xl bg-amber-500 text-white text-sm font-bold shadow-lg active:scale-[0.98] transition-transform" data-testid="filter-apply-btn">Apply Filters</button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── ACTIVE FILTER CHIPS BAR ────────────────────────────────────
export function ActiveFilterChips({ filters, onRemove, onClearAll }) {
  const chips = [];

  (filters.categories || []).forEach(c => chips.push({ key: `cat-${c}`, label: c, remove: () => onRemove("categories", c) }));
  (filters.groupProfile || []).forEach(g => chips.push({ key: `group-${g}`, label: GROUP_OPTIONS.find(o => o.value === g)?.label || g, remove: () => onRemove("groupProfile", g) }));
  (filters.seasonalTags || []).forEach(s => chips.push({ key: `season-${s}`, label: [...SEASON_OPTIONS, ...TIME_OPTIONS].find(o => o.value === s)?.label || s, remove: () => onRemove("seasonalTags", s) }));
  if (filters.vibe) chips.push({ key: "vibe", label: VIBE_OPTIONS.find(v => v.value === filters.vibe)?.label || filters.vibe, remove: () => onRemove("vibe") });
  if (filters.priceTier) chips.push({ key: "price", label: PRICE_OPTIONS.find(p => p.value === filters.priceTier)?.label || filters.priceTier, remove: () => onRemove("priceTier") });
  (filters.logisticsFlags || []).forEach(l => chips.push({ key: `log-${l}`, label: LOGISTICS_OPTIONS.find(o => o.value === l)?.label || l, remove: () => onRemove("logisticsFlags", l) }));
  if (filters.verifiedOnly) chips.push({ key: "verified", label: "Verified Only", remove: () => onRemove("verifiedOnly") });
  if (filters.newOnly) chips.push({ key: "new", label: "New", remove: () => onRemove("newOnly") });
  if (filters.openNow) chips.push({ key: "open", label: "Open Now", remove: () => onRemove("openNow") });

  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto py-2 px-1 no-scrollbar" data-testid="active-filter-chips">
      {chips.map(chip => (
        <button key={chip.key} onClick={chip.remove}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-medium whitespace-nowrap shrink-0 hover:bg-amber-200 transition"
          data-testid={`chip-${chip.key}`}>
          {chip.label}
          <X className="w-3 h-3" />
        </button>
      ))}
      <button onClick={onClearAll} className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium whitespace-nowrap shrink-0 hover:bg-slate-200" data-testid="chips-clear-all">Clear All</button>
    </div>
  );
}

export function loadFiltersFromSession() {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

export function clearFilterSession() {
  sessionStorage.removeItem(SESSION_KEY);
}
